/**
 * The plant picker's answers as the plant fields draw them (W24 Job B; the owner's rule 1: the backend decides, the app
 * displays). Where the fields used to download the whole catalogue and every variety (GET /plants/all, 130 KB, and GET
 * /varieties/all, 146 KB) and then group, sort, label and start them in the app, they now read three small answers, one
 * per step the gardener takes (api/plantPicker):
 *
 * - the crops answer when the Plant drop-down is drawn (usePickerCrops);
 * - one crop's types answer when a crop is chosen (useCropTypes): its Type list, whether it offers "Not sure" (B52), and
 *   what Type and Variety start on (B65), all the server's;
 * - one type's varieties answer when a type is chosen (useTypeVarieties): its Variety list, its hidden "Not sure" variety,
 *   what Variety starts on, and the names of the type and its crop.
 *
 * A place holding many rows (a packet scan, a photo of several kinds) reads one varieties answer per distinct type
 * (usePickerLookup), and asks the server which variety a name seen in a photo is (useSeenMatches, the `match`
 * parameter): the app matches no names itself.
 *
 * Nothing here sorts, labels, matches or decides a start: startingChoice and usePickStarts only apply the server's
 * `start` when a crop or type is picked in a list, never over a type or "Not sure" the gardener already chose for that
 * crop, and missingField says which field still needs an answer.
 *
 * Caching: every answer under [PLANT_PICKER_QUERY_KEY, 'crops' | 'types' | 'varieties' | 'match', gardenId, language,
 * id...], fresh for CATALOGUE_STALE_TIME_MS. gardenId is the garden being tended (chosenGarden), else the gardener's own,
 * as the search's key. Naming a variety or a plant of their own marks every answer stale (pickerStale).
 *
 * @module usePlantPicker
 */

import React from 'react';
import { useQueries, useQuery, useQueryClient, type QueryClient, type UseQueryResult } from '@tanstack/react-query';
import {
  getPickerCrops, getPickerTypes, getPickerVarieties,
  type PickerCrops, type PickerMatch, type PickerTypes, type PickerVarieties, type PickerVariety,
} from '@/shared/api/plantPicker';
import { useAuth } from '@/shared/hooks/useAuth';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { chosenGarden } from '@/shared/api/gardenMembers';

/** The first word of every picker answer's React Query key; invalidating it marks them all stale. @kept K1 */
export const PLANT_PICKER_QUERY_KEY = 'plant-picker';

/**
 * How long the picker's answers stay fresh in the client, in milliseconds. The catalogue changes rarely; a variety or
 * plant the gardener names marks the answers stale (pickerStale), so it does not wait for this. Also the packet
 * suggestion look-up's (features/packets/lib/acceptSuggestion).
 * @kept K1
 */
export const CATALOGUE_STALE_TIME_MS = 60 * 60 * 1000;

/** What the fields have chosen: a crop, one of its types, and a variety. */
export interface CropChoice {
  /** The crop: a plant group's id. */
  cropId: number | null;
  /** The type: a catalogue plant's id. */
  plantId: number | null;
  /** The variety: a named one, or the type's hidden "not sure". */
  varietyId: number | null;
  /**
   * Type is "Not sure" (B52): plantId and varietyId are then null and the server files the plant on the crop's usual
   * type. Present only when true.
   */
  typeUnsure?: boolean;
}

/** Nothing chosen. */
export const EMPTY_CHOICE: CropChoice = { cropId: null, plantId: null, varietyId: null };

/** Whose answers these are, and in which language: what every picker key carries after its kind. */
export interface PickerScope {
  /** The garden being tended, else the gardener's own; null before sign-in. */
  gardenId: number | null;
  /** The reader's language. */
  language: string;
  /** Someone is signed in: nothing is asked before. */
  signedIn: boolean;
}

/**
 * The garden and language the picker's answers are asked for.
 *
 * @returns PickerScope.
 */
export function usePickerScope(): PickerScope {
  const { mysqlUser } = useAuth();
  const { i18n: { language } } = useTranslation();
  return { gardenId: chosenGarden() ?? mysqlUser?.user_id ?? null, language, signedIn: Boolean(mysqlUser) };
}

/** The picker answers' query keys, by kind. */
export const pickerKeys = {
  /**
   * The crops answer's key.
   *
   * @param scope - Garden and language.
   */
  crops: (scope: PickerScope) => [PLANT_PICKER_QUERY_KEY, 'crops', scope.gardenId, scope.language] as const,
  /**
   * One crop's types answer's key.
   *
   * @param scope - Garden and language.
   * @param cropId - The crop.
   */
  types: (scope: PickerScope, cropId: number) => [PLANT_PICKER_QUERY_KEY, 'types', scope.gardenId, scope.language, cropId] as const,
  /**
   * One type's varieties answer's key.
   *
   * @param scope - Garden and language.
   * @param plantId - The type.
   */
  varieties: (scope: PickerScope, plantId: number) => [PLANT_PICKER_QUERY_KEY, 'varieties', scope.gardenId, scope.language, plantId] as const,
  /**
   * One type's varieties answer asked with a seen name to match.
   *
   * @param scope - Garden and language.
   * @param plantId - The type.
   * @param name - The name seen.
   */
  match: (scope: PickerScope, plantId: number, name: string) => [PLANT_PICKER_QUERY_KEY, 'match', scope.gardenId, scope.language, plantId, name] as const,
};

/**
 * The query for one type's varieties answer, for a read outside a component's draw (queryClient.fetchQuery).
 *
 * @param scope - Garden and language.
 * @param plantId - The type.
 * @returns Its key, reader and freshness.
 */
export const varietiesQuery = (scope: PickerScope, plantId: number): {
  queryKey: ReturnType<typeof pickerKeys.varieties>; queryFn: () => Promise<PickerVarieties>; staleTime: number;
} => ({
  queryKey: pickerKeys.varieties(scope, plantId),
  queryFn: () => getPickerVarieties(plantId, scope.language),
  staleTime: CATALOGUE_STALE_TIME_MS,
});

/**
 * Marks every picker answer stale, so the next draw asks again: a variety or plant of the gardener's own was named.
 *
 * @param queryClient - The app's client.
 * @returns The invalidation, to await where the caller waits for it.
 */
export const pickerStale = (queryClient: QueryClient): Promise<void> => queryClient.invalidateQueries({ queryKey: [PLANT_PICKER_QUERY_KEY] });

/**
 * The Plant drop-down's crops (GET /plants/picker/crops), as the server groups and orders them.
 *
 * @param enabled - False while no Plant drop-down is drawn.
 * @returns The query: its data is the crops answer.
 */
export function usePickerCrops(enabled = true): UseQueryResult<PickerCrops> {
  const scope = usePickerScope();
  return useQuery<PickerCrops>({
    queryKey: pickerKeys.crops(scope),
    queryFn: () => getPickerCrops(scope.language),
    enabled: enabled && scope.signedIn,
    staleTime: CATALOGUE_STALE_TIME_MS,
  });
}

/**
 * One crop's Type list and its start (GET /plants/picker/types), asked only once a crop is chosen.
 *
 * @param cropId - The crop chosen, or null for none.
 * @returns The query: its data is the types answer.
 */
export function useCropTypes(cropId: number | null): UseQueryResult<PickerTypes> {
  const scope = usePickerScope();
  return useQuery<PickerTypes>({
    queryKey: pickerKeys.types(scope, cropId ?? 0),
    queryFn: () => getPickerTypes(cropId as number, scope.language),
    enabled: scope.signedIn && cropId !== null,
    staleTime: CATALOGUE_STALE_TIME_MS,
  });
}

/**
 * One type's Variety list, its start and its names (GET /plants/picker/varieties), asked only once a type is chosen.
 *
 * @param plantId - The type chosen, or null for none.
 * @returns The query: its data is the varieties answer.
 */
export function useTypeVarieties(plantId: number | null): UseQueryResult<PickerVarieties> {
  const scope = usePickerScope();
  return useQuery<PickerVarieties>({
    ...varietiesQuery(scope, plantId ?? 0),
    enabled: scope.signedIn && plantId !== null,
  });
}

/** A type's varieties as a row reads them: the "Not sure" one, the named ones, and what Variety starts on. */
export interface TypeVarieties {
  /** The hidden "Not sure" variety's id, or null. */
  notSure: number | null;
  /** The named varieties, in the server's order. */
  named: PickerVariety[];
  /** What Variety starts on, or null. */
  start: number | null;
}

/** What usePickerLookup reads back for the rows it was given. */
export interface PickerLookup {
  /** Any answer still on its first read. */
  loading: boolean;
  /** A type's whole name, or null when it is not read (yet). */
  plantName: (plantId: number | null) => string | null;
  /** The crop a type is of, or null. */
  cropOf: (plantId: number | null) => number | null;
  /** A crop's name, or null. */
  cropName: (cropId: number | null) => string | null;
  /** A type's varieties (none for a type not read). */
  varietiesOf: (plantId: number | null) => TypeVarieties;
}

/** No varieties: a type not read, or none. */
const NO_VARIETIES: TypeVarieties = { notSure: null, named: [], start: null };

/**
 * The names and varieties of many rows' types at once (a packet scan, a photo of several kinds): the crops answer and
 * one varieties answer per distinct type. It only reads the answers back; nothing is matched, sorted or started.
 *
 * @param plantIds - The rows' types (null and repeats are left out).
 * @param enabled - False to ask nothing yet.
 * @returns PickerLookup.
 */
export function usePickerLookup(plantIds: ReadonlyArray<number | null | undefined>, enabled = true): PickerLookup {
  const scope = usePickerScope();
  const crops = usePickerCrops(enabled);
  const idsKey = [...new Set(plantIds.filter((id): id is number => typeof id === 'number'))].sort((a, b) => a - b).join(',');
  const ids = React.useMemo(() => (idsKey ? idsKey.split(',').map(Number) : []), [idsKey]);
  const answers = useQueries({
    queries: ids.map((id) => ({ ...varietiesQuery(scope, id), enabled: enabled && scope.signedIn })),
  });
  const loading = crops.isLoading || answers.some((answer) => answer.isLoading);
  // Read back on every draw (a few rows at most); the answers themselves are React Query's.
  const byPlant = new Map<number, PickerVarieties>();
  ids.forEach((id, at) => { const data = answers[at]?.data; if (data) byPlant.set(id, data); });
  /** Each type's whole name as its answer gives it (a catalogue type's, not a garden plant's). */
  const typeNames = new Map<number, string>();
  for (const [id, data] of byPlant) typeNames.set(id, data.plant.name);
  const cropNames = new Map<number, string>();
  for (const group of crops.data?.groups ?? []) for (const crop of group.crops) cropNames.set(crop.id, crop.name);
  for (const data of byPlant.values()) if (!cropNames.has(data.crop.id)) cropNames.set(data.crop.id, data.crop.name);
  return {
    loading,
    plantName: (plantId) => (plantId === null ? null : typeNames.get(plantId) ?? null),
    cropOf: (plantId) => (plantId === null ? null : byPlant.get(plantId)?.crop.id ?? null),
    cropName: (cropId) => (cropId === null ? null : cropNames.get(cropId) ?? null),
    varietiesOf: (plantId) => {
      const data = plantId === null ? undefined : byPlant.get(plantId);
      return data ? { notSure: data.notSure, named: data.varieties, start: data.start.varietyId } : NO_VARIETIES;
    },
  };
}

/** A name seen in a photo, under the type it was seen as. */
export interface SeenName {
  /** The type Grow saw it as, or null for none. */
  plantId: number | null;
  /** The variety name seen, or null for none. */
  name: string | null | undefined;
}

/** What useSeenMatches reads back. */
export interface SeenMatches {
  /** Any match still on its first read. */
  loading: boolean;
  /** The variety a seen name is (null for none); undefined while its answer is not in. */
  matchOf: (plantId: number | null, name: string | null | undefined) => PickerMatch | null | undefined;
}

/**
 * Which variety each name seen in a photo is (a tray kind's "Sun Gold", a plant tag's "Cherokee Purple"), as the server
 * matches it (the varieties answer with `match`: this type first, then the crop's other types; capitals, spaces and
 * marks ignored). The app compares no names itself (W24 Job B moved KindsPile's matching onto the server).
 *
 * @param seen - The names seen, each under its type; ones with no type or no name are left out.
 * @param enabled - False to ask nothing yet.
 * @returns SeenMatches.
 */
export function useSeenMatches(seen: ReadonlyArray<SeenName>, enabled = true): SeenMatches {
  const scope = usePickerScope();
  const pairs = seen.filter((each): each is { plantId: number; name: string } => each.plantId !== null && Boolean(each.name?.trim()));
  const answers = useQueries({
    queries: pairs.map(({ plantId, name }) => ({
      queryKey: pickerKeys.match(scope, plantId, name.trim()),
      queryFn: () => getPickerVarieties(plantId, scope.language, name.trim()),
      enabled: enabled && scope.signedIn,
      staleTime: CATALOGUE_STALE_TIME_MS,
    })),
  });
  return {
    loading: answers.some((answer) => answer.isLoading),
    matchOf: (plantId, name) => {
      const at = pairs.findIndex((pair) => pair.plantId === plantId && pair.name.trim() === name?.trim());
      return at === -1 || !answers[at]?.data ? undefined : answers[at].data?.matched ?? null;
    },
  };
}

/**
 * Which field, if any, still needs an answer. "Not sure" for Type is an answer (B52); Variety needs one only when the
 * type has named varieties, and while its answer is not in yet.
 *
 * @param choice - What is chosen so far.
 * @param varieties - The varieties answer for the chosen type, or undefined while it is not in.
 */
export function missingField(choice: CropChoice, varieties: PickerVarieties | undefined): 'plant' | 'type' | 'variety' | null {
  if (choice.cropId === null) return 'plant';
  if (choice.typeUnsure) return null;
  if (choice.plantId === null) return 'type';
  if (choice.varietyId === null && (varieties === undefined || varieties.varieties.length > 0)) return 'variety';
  return null;
}

/**
 * The choice a crop picked in a list amounts to (B65, B52): what the gardener already chose for this same crop (a type or
 * "Not sure"), unchanged; else the server's start for the crop, from its types answer; else, while that answer is not in,
 * the crop alone (usePickStarts applies the start when it arrives).
 *
 * @param cropId - The crop picked, or null for none.
 * @param current - What is chosen now, if anything.
 * @param types - The crop's types answer, or undefined while it is not in.
 * @returns The choice.
 */
export function startingChoice(cropId: number | null, current: CropChoice | undefined, types: PickerTypes | undefined): CropChoice {
  if (cropId === null) return EMPTY_CHOICE;
  if (current && current.cropId === cropId && (current.plantId !== null || current.typeUnsure)) return current;
  if (!types || types.crop.id !== cropId) return { cropId, plantId: null, varietyId: null };
  const { plantId, typeUnsure, varietyId } = types.start;
  return typeUnsure ? { cropId, plantId: null, varietyId: null, typeUnsure: true } : { cropId, plantId, varietyId };
}

/** What usePickStarts hands back: what a pick in a list amounts to now. */
export interface PickStarts {
  /** A crop picked: its start when its types answer is held, else the crop alone, the start applied when it arrives. */
  cropPicked: (cropId: number | null, current: CropChoice) => CropChoice;
  /** A type picked: Variety on its start when its varieties answer is held, else empty, the start applied when it arrives. */
  typePicked: (plantId: number | null, current: CropChoice) => CropChoice;
}

/**
 * Applies the server's starts to picks made in a list (the design's "picking a crop sets the crop at once; the Type and
 * Variety starts are the types answer's start, applied when that answer arrives, only while the choice still has this
 * crop and no type"; "picking a type sets Variety to its start"). A start is applied once per pick, and never over a
 * type, "Not sure" or variety chosen meanwhile.
 *
 * @param value - The choice now.
 * @param apply - Sets the choice (the caller's onChange or setter).
 * @returns PickStarts.
 */
export function usePickStarts(value: CropChoice, apply: (next: CropChoice) => void): PickStarts {
  const queryClient = useQueryClient();
  const scope = usePickerScope();
  /** The crop and the type picked whose answers are awaited, or null. */
  const pending = React.useRef<{ crop: number | null; type: number | null }>({ crop: null, type: null });
  const types = useCropTypes(value.cropId);
  const varieties = useTypeVarieties(value.plantId);

  React.useEffect(() => {
    const waiting = pending.current.crop;
    if (waiting === null) return;
    if (value.cropId !== waiting) { pending.current.crop = null; return; }
    if (!types.data || types.data.crop.id !== waiting) return;
    pending.current.crop = null;
    const next = startingChoice(waiting, value, types.data);
    if (next !== value) apply(next);
  }, [types.data, value, apply]);

  React.useEffect(() => {
    const waiting = pending.current.type;
    if (waiting === null) return;
    if (value.plantId !== waiting) { pending.current.type = null; return; }
    if (!varieties.data || varieties.data.plant.id !== waiting) return;
    pending.current.type = null;
    if (value.varietyId === null && varieties.data.start.varietyId !== null) apply({ ...value, varietyId: varieties.data.start.varietyId });
  }, [varieties.data, value, apply]);

  const { gardenId, language, signedIn } = scope;
  return React.useMemo((): PickStarts => {
    const here = { gardenId, language, signedIn };
    return {
      cropPicked: (cropId, current) => {
        const held = cropId === null ? undefined : queryClient.getQueryData<PickerTypes>(pickerKeys.types(here, cropId));
        const next = startingChoice(cropId, current, held);
        pending.current.crop = cropId !== null && !held && next.plantId === null && !next.typeUnsure ? cropId : null;
        return next;
      },
      typePicked: (plantId, current) => {
        const held = plantId === null ? undefined : queryClient.getQueryData<PickerVarieties>(pickerKeys.varieties(here, plantId));
        pending.current.type = plantId !== null && !held ? plantId : null;
        return { cropId: current.cropId, plantId, varietyId: held?.start.varietyId ?? null };
      },
    };
  }, [queryClient, gardenId, language, signedIn]);
}
